/**
 * Iter79 — Break-glass emergency recovery.
 *
 * UNAUTHENTICATED route. Reached when a user has lost their phone AND
 * every MFA backup code AND is either locked or unable to reset via
 * normal means.
 *
 * Flow:
 *   1. User enters email + one 20-char recovery code + a new password
 *      + hCaptcha (bypass in dev).
 *   2. Server verifies the code, resets password, wipes MFA, revokes
 *      every session, clears any admin lock, dispatches a CRITICAL
 *      alert to admins.
 *   3. User is redirected to /login where they sign in with the new
 *      password. They'll be forced to re-enroll MFA on next visit.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, ArrowLeft, Loader2 } from "lucide-react";

import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function EmergencyRecovery() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (newPassword !== confirmPassword) {
            setError("Passwords don't match.");
            return;
        }
        setBusy(true);
        try {
            const r = await api.post("/auth/emergency-recovery", {
                email: email.trim(),
                recovery_code: code.trim(),
                new_password: newPassword,
                captcha_token: null,  // hCaptcha widget skipped in this UI for MVP; server bypasses when not configured
            });
            toast.success("Recovery complete. Sign in with your new password.");
            setTimeout(() => navigate("/login"), 1500);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 429 && detail?.error === "rate_limited") {
                setError(detail.reason || "Too many attempts — try later.");
            } else if (err?.response?.status === 401) {
                setError("Recovery failed. Check your email + recovery code and try again.");
            } else if (typeof detail === "string") {
                setError(detail);
            } else {
                setError("Something went wrong. Please try again.");
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4"
            data-testid="emergency-recovery-page">
            <div className="w-full max-w-md bg-white rounded-lg shadow-lg border p-8 space-y-5">
                <div className="flex items-start gap-3">
                    <div className="p-2 rounded-md bg-rose-100">
                        <ShieldAlert className="h-6 w-6 text-rose-700" />
                    </div>
                    <div>
                        <h1 className="text-lg font-semibold asl-navy-text">Emergency recovery</h1>
                        <p className="text-xs text-slate-600 mt-0.5">
                            Use one of your saved recovery codes to reset your password and clear MFA.
                        </p>
                    </div>
                </div>

                <form onSubmit={submit} className="space-y-3.5">
                    <div>
                        <Label htmlFor="er-email" className="text-xs">Email</Label>
                        <Input
                            id="er-email" type="email" required
                            value={email} onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@asl.com"
                            data-testid="emergency-recovery-email"
                            autoComplete="email"
                        />
                    </div>
                    <div>
                        <Label htmlFor="er-code" className="text-xs flex items-center gap-1">
                            <KeyRound className="h-3 w-3" /> Recovery code (XXXX-XXXX-XXXX-XXXX-XXXX)
                        </Label>
                        <Input
                            id="er-code" required
                            value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder="ABCD-EFGH-IJKL-MNOP-QRST"
                            className="font-mono tracking-wider"
                            data-testid="emergency-recovery-code"
                            autoComplete="off" spellCheck={false}
                        />
                    </div>
                    <div>
                        <Label htmlFor="er-new" className="text-xs">New password</Label>
                        <Input
                            id="er-new" type="password" required
                            value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="At least 12 chars, upper+lower+digit+symbol"
                            data-testid="emergency-recovery-new-password"
                            autoComplete="new-password"
                        />
                    </div>
                    <div>
                        <Label htmlFor="er-confirm" className="text-xs">Confirm new password</Label>
                        <Input
                            id="er-confirm" type="password" required
                            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                            data-testid="emergency-recovery-confirm-password"
                            autoComplete="new-password"
                        />
                    </div>

                    {error && (
                        <div className="text-xs px-3 py-2 rounded border bg-rose-50 border-rose-300 text-rose-800"
                            data-testid="emergency-recovery-error">
                            {error}
                        </div>
                    )}

                    <div className="pt-1 flex items-center gap-2">
                        <Button
                            type="submit" className="asl-crimson-bg text-white flex-1"
                            disabled={busy || !email || !code || !newPassword}
                            data-testid="emergency-recovery-submit"
                        >
                            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                            Reset access
                        </Button>
                    </div>
                </form>

                <div className="pt-3 border-t text-xs text-slate-500 space-y-1">
                    <p>
                        Using a recovery code will <strong>clear MFA</strong> and <strong>revoke every session</strong>.
                        A CRITICAL alert is sent to your administrators.
                    </p>
                    <Link to="/login"
                        className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                        data-testid="emergency-recovery-back-to-login">
                        <ArrowLeft className="h-3 w-3" /> Back to sign in
                    </Link>
                </div>
            </div>
        </div>
    );
}
